/**
 * CenterPaddingSlider Component
 *
 * Slider control for adjusting the padding inside the center overlay area.
 * Allows users to control how much space surrounds logos/labels within the center.
 *
 * Requirements:
 * - Adjustable padding from 0% to 40%
 * - Visual feedback showing current percentage
 */

import './CenterPaddingSlider.css';

/**
 * Props for the CenterPaddingSlider component
 */
export interface CenterPaddingSliderProps {
  /** Current padding value as a ratio (0-0.4) */
  readonly padding: number;
  /** Callback fired when padding changes */
  readonly onChange: (padding: number) => void;
  /** Whether the control is disabled */
  readonly disabled?: boolean;
}

/** Minimum padding value (0%) */
const MIN_PADDING = 0;

/** Maximum padding value (40%) */
const MAX_PADDING = 0.4;

/** Step increment for the slider */
const STEP = 0.01;

/**
 * CenterPaddingSlider - Slider for adjusting center overlay padding
 *
 * Provides a range slider that allows users to adjust the padding
 * inside the white center area from 0% to 40%.
 *
 * @example
 * ```tsx
 * <CenterPaddingSlider
 *   padding={0.15}
 *   onChange={(padding) => updateTemplate({ centerPadding: padding })}
 * />
 * ```
 */
export function CenterPaddingSlider({
  padding,
  onChange,
  disabled = false,
}: CenterPaddingSliderProps): JSX.Element {
  // Convert ratio to percentage for display
  const percentageValue = Math.round(padding * 100);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = parseFloat(event.target.value);
    onChange(newValue);
  };

  return (
    <div className="center-padding-slider">
      <div className="center-padding-slider__header">
        <label
          htmlFor="center-padding"
          className="center-padding-slider__label"
        >
          Center Padding
        </label>
        <span className="center-padding-slider__value">{percentageValue}%</span>
      </div>
      <input
        id="center-padding"
        type="range"
        className="center-padding-slider__input"
        min={MIN_PADDING}
        max={MAX_PADDING}
        step={STEP}
        value={padding}
        onChange={handleChange}
        disabled={disabled}
        aria-label={`Center padding: ${percentageValue}%`}
      />
      <div className="center-padding-slider__labels">
        <span>0%</span>
        <span>40%</span>
      </div>
    </div>
  );
}
